import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

interface APIEndpoint {
    id: string;
    name: string;
    base_url: string;
    domain: string;
    auth_type: string;
    is_active: boolean;
    created_at: string;
}

interface APIManagementWidgetProps {
    onEdit?: (endpoint: APIEndpoint) => void;
    onTest?: (endpoint: APIEndpoint) => void;
    onCreateNew?: () => void;
}

const APIManagementWidget: React.FC<APIManagementWidgetProps> = ({
    onEdit,
    onTest,
    onCreateNew
}) => {
    const [endpoints, setEndpoints] = useState<APIEndpoint[]>([]);
    const [loading, setLoading] = useState(false);
    const [selectedEndpoint, setSelectedEndpoint] = useState<string>('');

    const fetchEndpoints = async () => {
        setLoading(true);
        try {
            // Change from /api-endpoint-manager/list-endpoints to /api/endpoints
            const response = await axios.get(`${API_BASE_URL}/api/endpoints`);
            if (response.data.success && response.data.endpoints) {
                setEndpoints(response.data.endpoints);
            } else if (response.data.endpoints) {
                // Fallback if success flag is missing
                setEndpoints(response.data.endpoints);
            }
        } catch (error) {
            console.error('Failed to fetch API endpoints:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchEndpoints();
    }, []);

    const handleTestEndpoint = async (endpoint: APIEndpoint) => {
        try {
            // Change from /api-endpoint-manager/execute-test to /api/endpoints/test
            const response = await axios.post(`${API_BASE_URL}/api/endpoints/test`, {
                endpoint_name: endpoint.name,
                test_name: "Health Check",
                path: "/posts/1",
                method: "GET",
                expected_status: 200
            });

            if (response.data.status === 'passed') {
                alert(`✅ API "${endpoint.name}" is working!\nResponse time: ${response.data.response_time_ms}ms`);
            } else {
                alert(`❌ API "${endpoint.name}" test failed.\nStatus: ${response.data.response_status}`);
            }
        } catch (error) {
            alert(`Failed to test API: ${error}`);
        }
    };

    const handleDeleteEndpoint = async (endpoint: APIEndpoint) => {
        if (!window.confirm(`Delete API endpoint "${endpoint.name}"?`)) return;

        try {
            // Change from /api-endpoint-manager/endpoints/ to /api/endpoints/
            await axios.delete(`${API_BASE_URL}/api/endpoints/${endpoint.id}`);
            fetchEndpoints();
            alert(`API endpoint "${endpoint.name}" deleted successfully`);
        } catch (error) {
            alert(`Failed to delete endpoint: ${error}`);
        }
    };

    const handleGenerateTests = async (endpoint: APIEndpoint) => {
        alert(`Test generation for API "${endpoint.name}" - Coming soon!`);
        // TODO: Trigger test generation for this API endpoint
    };

    return (
        <div className="dashboard-widget" style={{ minHeight: '400px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <h3>🔗 API Endpoints ({endpoints.length})</h3>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                        className="pushable push-success btn-sm"
                        onClick={onCreateNew}
                    >
                        <span className="shadow"></span>
                        <span className="edge"></span>
                        <span className="front">+ Add API</span>
                    </button>
                    <button
                        className="pushable push-primary btn-sm"
                        onClick={fetchEndpoints}
                        disabled={loading}
                    >
                        <span className="shadow"></span>
                        <span className="edge"></span>
                        <span className="front">🔄 Refresh</span>
                    </button>
                </div>
            </div>

            {loading ? (
                <div style={{ textAlign: 'center', padding: '2rem' }}>
                    Loading API endpoints...
                </div>
            ) : endpoints.length === 0 ? (
                <div style={{
                    textAlign: 'center',
                    padding: '3rem',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '8px',
                    border: '2px dashed #dee2e6'
                }}>
                    <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>🔗</div>
                    <p style={{ color: '#6c757d', marginBottom: '1rem' }}>
                        No API endpoints configured yet.
                    </p>
                    <button
                        className="pushable push-primary"
                        onClick={onCreateNew}
                    >
                        <span className="shadow"></span>
                        <span className="edge"></span>
                        <span className="front">Configure Your First API</span>
                    </button>
                </div>
            ) : (
                <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
                    {endpoints.map(endpoint => (
                        <div
                            key={endpoint.id}
                            style={{
                                padding: '1rem',
                                border: '1px solid #ddd',
                                borderRadius: '6px',
                                marginBottom: '0.75rem',
                                backgroundColor: endpoint.is_active ? '#fff' : '#f8f9fa'
                            }}
                        >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <div style={{ flex: 1 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                                        <strong style={{ fontSize: '1.1rem' }}>{endpoint.name}</strong>
                                        <span style={{
                                            padding: '0.2rem 0.5rem',
                                            borderRadius: '12px',
                                            fontSize: '0.75rem',
                                            backgroundColor: endpoint.is_active ? '#d4edda' : '#f8d7da',
                                            color: endpoint.is_active ? '#155724' : '#721c24',
                                            fontWeight: 'bold'
                                        }}>
                                            {endpoint.is_active ? 'ACTIVE' : 'INACTIVE'}
                                        </span>
                                        <span style={{
                                            padding: '0.2rem 0.5rem',
                                            borderRadius: '12px',
                                            fontSize: '0.75rem',
                                            backgroundColor: '#e3f2fd',
                                            color: '#1976d2'
                                        }}>
                                            {endpoint.domain}
                                        </span>
                                        {endpoint.auth_type !== 'none' && (
                                            <span style={{
                                                padding: '0.2rem 0.5rem',
                                                borderRadius: '12px',
                                                fontSize: '0.75rem',
                                                backgroundColor: '#fff3cd',
                                                color: '#856404'
                                            }}>
                                                🔐 {endpoint.auth_type}
                                            </span>
                                        )}
                                    </div>
                                    <div style={{ fontSize: '0.9rem', color: '#6c757d', marginBottom: '0.25rem' }}>
                                        <strong>URL:</strong> {endpoint.base_url}
                                    </div>
                                    <div style={{ fontSize: '0.8rem', color: '#6c757d' }}>
                                        <strong>Created:</strong> {new Date(endpoint.created_at).toLocaleDateString()}
                                    </div>
                                </div>

                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                                    <button
                                        className="pushable push-primary btn-sm"
                                        onClick={() => handleTestEndpoint(endpoint)}
                                        title="Test API connection"
                                    >
                                        <span className="shadow"></span>
                                        <span className="edge"></span>
                                        <span className="front">🧪 Test</span>
                                    </button>
                                    <button
                                        className="pushable push-success btn-sm"
                                        onClick={() => handleGenerateTests(endpoint)}
                                        title="Generate test cases"
                                    >
                                        <span className="shadow"></span>
                                        <span className="edge"></span>
                                        <span className="front">⚡ Generate</span>
                                    </button>
                                    <button
                                        className="pushable push-warning btn-sm"
                                        onClick={() => onEdit?.(endpoint)}
                                        title="Edit endpoint"
                                    >
                                        <span className="shadow"></span>
                                        <span className="edge"></span>
                                        <span className="front">✏️ Edit</span>
                                    </button>
                                    <button
                                        className="pushable push-danger btn-sm"
                                        onClick={() => handleDeleteEndpoint(endpoint)}
                                        title="Delete endpoint"
                                    >
                                        <span className="shadow"></span>
                                        <span className="edge"></span>
                                        <span className="front">🗑️</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default APIManagementWidget;